import { createFileRoute, Link } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { Search, MapPin, Briefcase, TrendingUp, ArrowRight } from "lucide-react";
import {
  POSTS,
  CATEGORIES,
  searchPosts,
  getPopularPosts,
  getCityGuides,
  getSectorGuides,
} from "@/data/blog";
import { BlogShell } from "@/components/blog/BlogLayout";
import { BlogCard } from "@/components/blog/BlogCard";
import { ArticleCTA, NewsletterBox } from "@/components/blog/ArticleCTA";

const searchSchema = z.object({
  q: fallback(z.string(), "").default(""),
  cat: fallback(z.string(), "").default(""),
});

export const Route = createFileRoute("/blog/")({
  validateSearch: zodValidator(searchSchema),
  head: () => ({
    meta: [
      { title: "Blog FlashWeb | Siti Web, SEO e Business in Ticino e Italia" },
      { name: "description", content: "Guide, casi studio e strategie per realizzare siti web professionali in Svizzera e Italia. Lugano, Bellinzona, Locarno, Como, Milano." },
      { property: "og:title", content: "Blog FlashWeb | Siti Web, SEO e Business in Ticino e Italia" },
      { property: "og:description", content: "Guide pratiche su siti web professionali, SEO locale e business online in Ticino e Lombardia." },
      { property: "og:url", content: "https://www.flash-web.ch/blog" },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://www.flash-web.ch/blog" }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Blog",
        name: "Blog FlashWeb",
        url: "https://www.flash-web.ch/blog",
        description: "Guide su siti web, SEO e business online in Svizzera e Italia.",
      }),
    }],
  }),
  component: BlogIndex,
});

function BlogIndex() {
  const { q, cat } = Route.useSearch();
  const navigate = Route.useNavigate();
  const isFiltered = Boolean(q) || Boolean(cat);
  const filtered = searchPosts(q, cat || undefined);
  const popular = getPopularPosts(6);
  const cities = getCityGuides(8);
  const sectors = getSectorGuides(8);
  const hero = POSTS[0];
  const subFeatured = POSTS.slice(1, 4);
  const latest = POSTS.slice(4);

  return (
    <BlogShell>
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden border-b border-border/40">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(900px_circle_at_15%_0%,oklch(0.7_0.15_280/.18),transparent_60%)]" />
        <div className="absolute right-0 top-0 -z-10 h-[420px] w-[420px] rounded-full bg-primary/10 blur-[120px]" />
        <div className="mx-auto max-w-7xl px-6 pt-14 pb-10 sm:pt-20">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur">
            <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_10px_var(--primary)]" />
            FlashWeb · Magazine
          </div>
          <h1 className="mt-5 max-w-4xl font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl">
            La rivista digitale per chi costruisce <span className="text-gradient">business sul web</span>.
          </h1>
          <p className="mt-5 max-w-2xl text-base text-muted-foreground sm:text-lg">
            Guide pratiche, casi studio e strategie SEO per imprenditori, studi e PMI in Ticino, Svizzera italiana, Como, Varese e Milano. Niente fuffa: solo contenuti che generano clienti.
          </p>

          {/* Search */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const value = (e.currentTarget.elements.namedItem("q") as HTMLInputElement).value;
              navigate({ search: (prev: { q: string; cat: string }) => ({ ...prev, q: value }) });
            }}
            className="mt-8 flex max-w-xl items-center gap-2 rounded-full border border-border bg-card/70 px-5 py-3 backdrop-blur-xl shadow-lg shadow-black/5"
          >
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              name="q"
              defaultValue={q}
              placeholder="Cerca: 'sito web Lugano', 'SEO ristorante', 'quanto costa'…"
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
            <button type="submit" className="rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90 transition">
              Cerca
            </button>
          </form>

          {/* Categories */}
          <div className="mt-6 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => navigate({ search: () => ({ q: "", cat: "" }) })}
              className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-all ${cat === "" && !q ? "border-primary bg-primary text-primary-foreground shadow-md shadow-primary/30" : "border-border text-muted-foreground hover:border-primary/60 hover:text-foreground"}`}
            >
              Tutte
            </button>
            {CATEGORIES.map((c) => (
              <button
                key={c.slug}
                type="button"
                onClick={() => navigate({ search: (prev: { q: string; cat: string }) => ({ ...prev, cat: c.slug }) })}
                className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-all ${cat === c.slug ? "border-primary bg-primary text-primary-foreground shadow-md shadow-primary/30" : "border-border text-muted-foreground hover:border-primary/60 hover:text-foreground"}`}
              >
                {c.name}
              </button>
            ))}
          </div>

          {/* Stats */}
          <div className="mt-10 grid max-w-3xl grid-cols-3 gap-6 border-t border-border/60 pt-6">
            <Stat n={String(POSTS.length)} label="Articoli pubblicati" />
            <Stat n="6+" label="Città coperte" />
            <Stat n="10+" label="Settori specializzati" />
          </div>
        </div>
      </section>

      {/* ============ FILTERED RESULTS ============ */}
      {isFiltered ? (
        <section className="mx-auto max-w-7xl px-6 py-16">
          <p className="mb-8 text-sm text-muted-foreground">
            {filtered.length} risultati {q && <>per "<span className="text-foreground font-semibold">{q}</span>"</>}
            {cat && <> in <span className="text-foreground font-semibold">{CATEGORIES.find(c => c.slug === cat)?.name}</span></>}
          </p>
          {filtered.length > 0 ? (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((p) => <BlogCard key={p.slug} post={p} />)}
            </div>
          ) : (
            <div className="rounded-3xl border border-dashed border-border p-12 text-center">
              <p className="text-muted-foreground">Nessun articolo trovato. Prova un'altra ricerca.</p>
              <Link to="/blog" search={{ q: "", cat: "" }} className="mt-4 inline-block rounded-full border border-border px-4 py-2 text-sm hover:border-primary">
                Reset filtri
              </Link>
            </div>
          )}
          <div className="mt-16"><NewsletterBox /></div>
        </section>
      ) : (
        <>
          {/* ============ HERO ARTICLE ============ */}
          {hero && (
            <section className="mx-auto max-w-7xl px-6 pt-14">
              <SectionHeader eyebrow="In primo piano" title="L'articolo del momento" />
              <div className="mt-8">
                <BlogCard post={hero} variant="hero" />
              </div>
            </section>
          )}

          {/* ============ SUB-FEATURED 3-UP ============ */}
          {subFeatured.length > 0 && (
            <section className="mx-auto max-w-7xl px-6 pt-20">
              <div className="grid gap-6 md:grid-cols-3">
                {subFeatured.map((p) => <BlogCard key={p.slug} post={p} />)}
              </div>
            </section>
          )}

          {/* ============ GUIDE PER CITTÀ ============ */}
          <section className="mx-auto max-w-7xl px-6 pt-24">
            <SectionHeader
              eyebrow="Guide per città"
              title="Sito web per la tua zona"
              description="Strategie locali per Ticino, Svizzera italiana e nord Italia."
              icon={<MapPin className="h-4 w-4" />}
            />
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {cities.map((p) => <BlogCard key={p.slug} post={p} variant="compact" />)}
            </div>
          </section>

          {/* ============ INLINE CTA ============ */}
          <section className="mx-auto max-w-5xl px-6 pt-20">
            <ArticleCTA />
          </section>

          {/* ============ GUIDE PER SETTORE ============ */}
          <section className="mx-auto max-w-7xl px-6 pt-20">
            <SectionHeader
              eyebrow="Guide per settore"
              title="Sito web per la tua attività"
              description="Best practice per ristoranti, studi, cliniche, immobiliari e PMI locali."
              icon={<Briefcase className="h-4 w-4" />}
            />
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {sectors.map((p) => <BlogCard key={p.slug} post={p} variant="compact" />)}
            </div>
          </section>

          {/* ============ PIÙ LETTI ============ */}
          <section className="mx-auto max-w-7xl px-6 pt-24">
            <SectionHeader
              eyebrow="Più letti"
              title="Le guide che gli imprenditori amano di più"
              icon={<TrendingUp className="h-4 w-4" />}
            />
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {popular.slice(0, 6).map((p) => <BlogCard key={p.slug} post={p} />)}
            </div>
          </section>

          {/* ============ LATEST FEED ============ */}
          <section className="mx-auto max-w-7xl px-6 py-24">
            <SectionHeader eyebrow="Archivio" title="Tutti gli articoli" />
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {latest.map((p, idx) => (
                <div key={p.slug} className="contents">
                  <BlogCard post={p} />
                  {(idx + 1) % 3 === 0 && idx !== latest.length - 1 && (
                    <div className="sm:col-span-2 lg:col-span-3">
                      <ArticleCTA />
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-20">
              <NewsletterBox />
            </div>
          </section>
        </>
      )}
    </BlogShell>
  );
}

function Stat({ n, label }: { n: string; label: string }) {
  return (
    <div>
      <div className="font-display text-3xl font-bold text-foreground sm:text-4xl">{n}</div>
      <div className="mt-1 text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}

function SectionHeader({
  eyebrow,
  title,
  description,
  icon,
}: {
  eyebrow: string;
  title: string;
  description?: string;
  icon?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-primary">
          {icon}
          {eyebrow}
        </span>
        <h2 className="mt-2 font-display text-3xl font-bold tracking-tight sm:text-4xl">{title}</h2>
        {description && <p className="mt-2 max-w-2xl text-sm text-muted-foreground sm:text-base">{description}</p>}
      </div>
      <Link to="/blog" search={{ q: "", cat: "" }} className="hidden items-center gap-2 text-sm text-muted-foreground hover:text-foreground sm:inline-flex">
        Vedi tutto <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}
